import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import { compile } from 'path-to-regexp';
import { stringify, StringifyOptions } from 'query-string';
import { CreateLeadOptIn } from '../common/components/CreateRemoteEntity/CreateLeadOptIn';
import { AdsAnalytics } from '../editor/features/beta/modules/Analytics/AdsAnalytics/AdsAnalytics';
import { BotAnalytics } from '../editor/features/beta/modules/Analytics/BotAnalytics/BotAnalytics';
import { EmailAnalytics } from '../editor/features/beta/modules/Analytics/EmailAnalytics/EmailAnalytics';
import { XRayAnalytics } from '../editor/features/beta/modules/Analytics/XRayAnalytics/XRayAnalytics';
import { Audiences } from '../editor/features/beta/modules/Audiences/Audiences';
import { AutomationRules } from '../editor/features/beta/modules/AutomationRules/AutomationRules';
import { UIFilterAutomationRuleKindType } from '../editor/features/beta/modules/AutomationRules/types';
import { AutomationEditRouter } from '../editor/features/beta/modules/Automations/AutomationEdit/AutomationEditRouter';
import { AutomationHome } from '../editor/features/beta/modules/Automations/AutomationHome/AutomationHome';
import { AutomationNew } from '../editor/features/beta/modules/Automations/AutomationNew/AutomationNew';
import { Campaigns } from '../editor/features/beta/modules/Campaigns/Campaigns';
import { UIFilterCampaignKindType } from '../editor/features/beta/modules/Campaigns/types';
import { ChatStarters } from '../editor/features/beta/modules/ChatStarters/ChatStarters';
import { Contacts } from '../editor/features/beta/modules/Contacts/Contacts';
import {
  AutomationsDashboard,
  Dashboard,
} from '../editor/features/beta/modules/Dashboard/Dashboard';
import { LeadsSearch } from '../editor/features/beta/modules/GetLeads/LeadsSearch/LeadsSearch';
import { XRayLeads } from '../editor/features/beta/modules/GetLeads/XRay/XRayLeads';
import { IntegrationsPanel } from '../editor/features/beta/modules/Integrations/IntegrationsPanel/IntegrationsPanel';
import { Restore } from '../editor/features/beta/modules/Restore';
import { Settings } from '../editor/features/beta/modules/Settings/Settings';
import ChatbotBuildPanel from '../editor/features/build/panels/ChatbotBuildPanel/ChatbotBuildPanel';
import CampaignReportPanel from '../editor/features/campaigns/panels/CampaignReportPanel/CampaignReportPanel';
import { ChatBlasterDetailsPanel } from '../editor/features/campaigns/panels/ChatBlasterDetailsPanel/ChatBlasterDetailsPanel';
import DripCampaignDetailsPanel from '../editor/features/campaigns/panels/DripCampaignDetailsPanel/DripCampaignDetailsPanel';
import { CtmAdDetail } from '../editor/features/clickToMessengerAds/components/CtmAdDetail/CtmAdDetail';
import EditConnectionPanel from '../editor/features/connections/panels/EditConnectionPanel/EditConnectionPanel';
import { FacebookPageWelcomerOverview } from '../editor/features/facebookPageWelcomer/FacebookPageWelcomerOverview';
import { InstagramWelcomerOverview } from '../editor/features/instagramWelcomer/InstagramWelcomerOverview';
import { LandingPageDetails } from '../editor/features/landingPages/components/LandingPageDetails/LandingPageDetails';
import { LiveChat } from '../editor/features/liveChat/LiveChatPanel';
import MessengerAdsPanel from '../editor/features/messengerAds/panels/MessengerAdsPanel/MessengerAdsPanel';
import { PromoBlasterDetails } from '../editor/features/promoBlasters/components/PromoBlasterDetails/PromoBlasterDetails';
import { EditPromoterPanel } from '../editor/features/promote/EditPromoterPanel/EditPromoterPanel';
import PromotersPanel from '../editor/features/promote/PromotersPanel/PromotersPanel';
import { LeadsPromoterPanel } from '../editor/features/promote/types/leads/LeadsPromoterPanel';
import { LeadsPromoterPanelEdit } from '../editor/features/promote/types/leads/LeadsPromoterPanelEdit';
import RssFeedDetails from '../editor/features/rssFeeds/components/RssFeedDetails/RssFeedDetails';
import RssFeedsPanel from '../editor/features/rssFeeds/panels/RssFeedsPanel/RssFeedsPanel';
import { EditSMSKeywordPanel } from '../editor/features/smsKeywords/EditSMSKeywordPanel/EditSMSKeywordPanel';
import { CommentGuardPanel } from '../editor/features/startBotPromoters/commentGuards/panels/CommentGuardPanel/CommentGuardPanel';
import EditCommentGuardPanel from '../editor/features/startBotPromoters/commentGuards/panels/EditCommentGuardPanel/EditCommentGuardPanel';
import { CustomerChatEditor } from '../editor/features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatEditor';
import { CustomerChatPanel } from '../editor/features/startBotPromoters/customerChat/CustomerChatPanel/CustomerChatPanel';
import { InstagramCommentGuardEditor } from '../editor/features/startBotPromoters/instagramCommentGuard/InstagramCommentGuardEditor';
import { InstagramLive } from '../editor/features/startBotPromoters/instagramLive/InstagramLive';
import { InstagramStoryMentionEditor } from '../editor/features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import { InstagramStoryReaction } from '../editor/features/startBotPromoters/instagramStoryReaction/InstagramStoryReaction';
import { TriggerPanel } from '../editor/features/trigger/panels/TriggerPanel/TriggerPanel';

type StartBotEditGetLinkParams = {
  chatbotId: number;
  promoterId: number;
  dialogueId: number;
};

export const CHATBOT_EDITOR_PATH = '/chatbot-editor/:chatbotId';
export const LIVE_CHAT_PATH = `/chatbot-editor/:chatbotId/live-chat`;

const LEAD_POPUPS_PATH = `${CHATBOT_EDITOR_PATH}/optin-popups`;
const LEAD_POPUPS_EDIT_PATH = `${LEAD_POPUPS_PATH}/edit/:dialogueId/:promoterId`;
const LEAD_POPUPS_NEW_PATH = `${LEAD_POPUPS_PATH}/new`;

const CHAT_STARTERS_PATH = `${CHATBOT_EDITOR_PATH}/chat-starters`;
const AUTOMATION_RULES_PATH = `${CHATBOT_EDITOR_PATH}/automation-rules`;
const CAMPAIGNS_PATH = `${CHATBOT_EDITOR_PATH}/campaigns`;
const AUDIENCES_PATH = `${CHATBOT_EDITOR_PATH}/audiences`;
const CONTACTS_PATH = `${CHATBOT_EDITOR_PATH}/contacts`;
const XRAY_PATH = `${CHATBOT_EDITOR_PATH}/x-ray`;
const MERCURY_PATH = `${CHATBOT_EDITOR_PATH}/mercury`;
const INTEGRATIONS_PATH = `${CHATBOT_EDITOR_PATH}/integrations`;

const RESTORE_PATH = `${CHATBOT_EDITOR_PATH}/restore`;

const CHAT_BLASTS_EDIT_PATH = `${CHATBOT_EDITOR_PATH}/chat-blaster/:campaignId/:dialogueId?`;

const SPONSORED_MESSAGES_EDIT_PATH = `${CHATBOT_EDITOR_PATH}/:chatbotId/sponsored-messages/edit/:id`;

const COMMENT_GUARD_PATH = `${CHATBOT_EDITOR_PATH}/comment-guards`;
const COMMENT_GUARD_EDIT_PATH = `${COMMENT_GUARD_PATH}/edit-comment-guard/:dialogueId/:promoterId`;

const CUSTOMER_CHAT_PATH = `${CHATBOT_EDITOR_PATH}/customer-chat`;
const CUSTOMER_CHAT_EDIT_PATH = `${CUSTOMER_CHAT_PATH}/edit/:dialogueId/:promoterId`;

const SHARE_LINKS_PATH = `${CHATBOT_EDITOR_PATH}/share-links`;
// todo-opt: identify if promoter param can go second, use one const for promoter edit path routes
const SHARE_LINKS_EDIT_PATH = `${SHARE_LINKS_PATH}/edit/:promoterId/:dialogueId`;

const SMS_KEYWORD_PATH = `${CHATBOT_EDITOR_PATH}/sms-keywords`;
const SMS_KEYWORD_EDIT_PATH = `${SMS_KEYWORD_PATH}/edit/:dialogueId/:smsKeywordId`;

const INSTAGRAM_STORY_MENTION_PATH = `${CHATBOT_EDITOR_PATH}/instagram-story-mentions`;
const INSTAGRAM_STORY_MENTION_EDIT_PATH = `${INSTAGRAM_STORY_MENTION_PATH}/edit/:dialogueId/:promoterId`;

const INSTAGRAM_STORY_REACTION_PATH = `${CHATBOT_EDITOR_PATH}/instagram-story-reactions`;
const INSTAGRAM_STORY_REACTION_EDIT_PATH = `${INSTAGRAM_STORY_REACTION_PATH}/edit/:dialogueId/:promoterId`;

const INSTAGRAM_COMMENT_GUARD_PATH = `${CHATBOT_EDITOR_PATH}/instagram-comment-guard`;
const INSTAGRAM_COMMENT_GUARD_EDIT_PATH = `${INSTAGRAM_COMMENT_GUARD_PATH}/edit/:dialogueId/:promoterId`;

const INSTAGRAM_LIVE_PATH = `${CHATBOT_EDITOR_PATH}/instagram-live`;
const INSTAGRAM_LIVE_EDIT_PATH = `${INSTAGRAM_LIVE_PATH}/edit/:dialogueId/:promoterId`;

const FACEBOOK_PAGE_WELCOMER = `${CHATBOT_EDITOR_PATH}/facebook-page-welcomer`;

const FACEBOOK_OPTINS_PATH = `${CHATBOT_EDITOR_PATH}/facebook-opt-ins`;

const SEND_TO_MESSENGER_PATH = `${FACEBOOK_OPTINS_PATH}/messenger/edit/:promoterId/:dialogueId`;
const CHECKBOCX_PLUGIN_PATH = `${FACEBOOK_OPTINS_PATH}/checkbox/edit/:promoterId/:dialogueId`;

const LANDING_PAGES_EDIT_PATH = `${CHATBOT_EDITOR_PATH}/landing-pages/edit/:landingPageId`;

const SETTINGS_PATH = `${CHATBOT_EDITOR_PATH}/settings`;
const SETTINGS_CHATBOT_PATH = `${SETTINGS_PATH}/chatbot`;
const SETTINGS_INBOX_PATH = `${SETTINGS_PATH}/inbox`;
const SETTINGS_CUSTOMER_CHAT_WIDGET_PATH = `${SETTINGS_PATH}/customer-chat-widget`;
const SETTINGS_SYSTEM_MESSAGES_PATH = `${SETTINGS_PATH}/system-messages`;
const SETTINGS_ADVERTISING_PATH = `${SETTINGS_PATH}/advertising`;
const SETTINGS_USERS_PATH = `${SETTINGS_PATH}/users`;
const SETTINGS_MAINTENANCE_PATH = `${SETTINGS_PATH}/maintenance`;
const SETTINGS_SMS_PATH = `${SETTINGS_PATH}/sms`;
const SETTINGS_SMS_ACTIVATE_PATH = `${SETTINGS_SMS_PATH}/activate`;
const SETTINGS_SMS_VIEW_NUMBER_PATH = `${SETTINGS_SMS_PATH}/number`;
const SETTINGS_SMS_AREA_CODE_SEARCH_PATH = `${SETTINGS_SMS_PATH}/area-code-search`;
const SETTINGS_TEMPLATES_PATH = `${SETTINGS_PATH}/templates`;
const SETTINGS_MM_API_PATH = `${SETTINGS_PATH}/public-api`;
const TEMPLATES_LIBRARY_PATH = `${SETTINGS_TEMPLATES_PATH}/library`;
const TEMPLATES_FIND_BY_CODE_PATH = `${SETTINGS_TEMPLATES_PATH}/find-by-code`;
const MY_TEMPLATES_PATH = `${SETTINGS_TEMPLATES_PATH}/my-templates`;
const RSS_FEEDS_PATH = '/chatbot-editor/:chatbotId/rss-feeds';
const RSS_FEED_PATH = '/chatbot-editor/:chatbotId/rss-feeds/edit/:rssFeedId';
const CAMPAIGN_REPORT_PATH =
  '/chatbot-editor/:chatbotId/campaign-report/:campaignId';
const CONNECTIONS_EDIT_PATH = `${CHATBOT_EDITOR_PATH}/edit-connection/:connectionId`;

const DASHBOARD_PATH = `${CHATBOT_EDITOR_PATH}/dashboard/:verify?`;
const AUTOMATIONS_DASHBOARD_PATH = `${CHATBOT_EDITOR_PATH}/adashboard/:verify?`;
const CHATBOT_HOME_PATH = `${CHATBOT_EDITOR_PATH}/home`;

const BOT_ANALYTICS_PATH = `${CHATBOT_EDITOR_PATH}/bot-analytics`;
const ADS_ANALYTICS_PATH = `${CHATBOT_EDITOR_PATH}/ads-analytics`;
const EMAIL_ANALYTICS_PATH = `${CHATBOT_EDITOR_PATH}/outreach-analytics`;
const XRAY_ANALYTICS_PATH = `${CHATBOT_EDITOR_PATH}/xray-analytics`;

const INSTAGRAM_WELCOMER_PATH = `${CHATBOT_EDITOR_PATH}/instagram-welcomer`;
const INSTAGRAM_WELCOMER__EDIT_PATH = `${INSTAGRAM_WELCOMER_PATH}/edit/:dialogueId/:promoterId`;

export const AUTOMATIONS_OLD_ROOT_PATH = `${CHATBOT_EDITOR_PATH}/automations/:view?`;
const AUTOMATIONS_HOME_PATH = `${CHATBOT_EDITOR_PATH}/automations/my`;
const AUTOMATIONS_NEW_PATH = `${CHATBOT_EDITOR_PATH}/automations/new`;
export const AUTOMATIONS_EDIT_PATH = `${CHATBOT_EDITOR_PATH}/automations/:id/edit`;

export const BotBuilderRoutes = {
  home: {
    path: CHATBOT_HOME_PATH,
    getLink: compile<{ chatbotId: number }>(CHATBOT_HOME_PATH),
  },
  index: {
    path: CHATBOT_EDITOR_PATH,
    getLink: compile<{ chatbotId: number }>(CHATBOT_EDITOR_PATH),
  },
  dashboard: {
    path: DASHBOARD_PATH,
    getLink: compile<{ chatbotId: number }>(DASHBOARD_PATH),
    component: Dashboard,
  },

  adashboard: {
    path: AUTOMATIONS_DASHBOARD_PATH,
    getLink: compile<{ chatbotId: number }>(AUTOMATIONS_DASHBOARD_PATH),
    component: AutomationsDashboard,
  },

  automationHome: {
    path: AUTOMATIONS_HOME_PATH,
    component: AutomationHome,
    getLink: compile<{ chatbotId: number }>(AUTOMATIONS_HOME_PATH),
  },
  automationNew: {
    path: AUTOMATIONS_NEW_PATH,
    component: AutomationNew,
    getLink: compile<{ chatbotId: number }>(AUTOMATIONS_NEW_PATH),
  },
  automationEdit: {
    path: AUTOMATIONS_EDIT_PATH,
    component: AutomationEditRouter,
    getLink: compile<{ chatbotId: number; id: number }>(AUTOMATIONS_EDIT_PATH),
  },
  build: {
    path: '/chatbot-editor/:chatbotId/build/:pageId?',
    component: ChatbotBuildPanel,
    getLink: ({
      chatbotId,
      dialogueId,
    }: {
      chatbotId: number;
      dialogueId?: number | string;
    }) => {
      const conditionalPath = dialogueId ? `/${dialogueId}` : '';
      return `/chatbot-editor/${chatbotId}/build${conditionalPath}`;
    },
  },
  qa: {
    path: '/chatbot-editor/:chatbotId/trigger',
    component: TriggerPanel,
    getLink: ({ chatbotId }: { chatbotId: number }) =>
      `/chatbot-editor/${chatbotId}/trigger`,
  },
  mainMenu: {
    path: '/chatbot-editor/:chatbotId/build/main-menu',
    component: ChatbotBuildPanel,
    getLink: ({ chatbotId }: { chatbotId: number }) => {
      return `/chatbot-editor/${chatbotId}/build/main-menu`;
    },
  },
  customerChat: {
    path: CUSTOMER_CHAT_PATH,
    component: CustomerChatPanel,
    getLink: compile<{ chatbotId: number }>(CUSTOMER_CHAT_PATH),
  },
  customerChatEdit: {
    path: CUSTOMER_CHAT_EDIT_PATH,
    component: CustomerChatEditor,
    getLink: compile<StartBotEditGetLinkParams>(CUSTOMER_CHAT_EDIT_PATH),
  },
  instagramStoryMention: {
    path: INSTAGRAM_STORY_MENTION_PATH,
    component: PromotersPanel,
    getLink: compile<StartBotEditGetLinkParams>(INSTAGRAM_STORY_MENTION_PATH),
  },
  instagramStoryMentionEdit: {
    path: INSTAGRAM_STORY_MENTION_EDIT_PATH,
    component: InstagramStoryMentionEditor,
    getLink: compile<StartBotEditGetLinkParams>(
      INSTAGRAM_STORY_MENTION_EDIT_PATH,
    ),
  },
  instagramStoryReactionEdit: {
    path: INSTAGRAM_STORY_REACTION_EDIT_PATH,
    component: InstagramStoryReaction,
    getLink: compile<StartBotEditGetLinkParams>(
      INSTAGRAM_STORY_REACTION_EDIT_PATH,
    ),
  },
  instagramCommentGuardEdit: {
    path: INSTAGRAM_COMMENT_GUARD_EDIT_PATH,
    component: InstagramCommentGuardEditor,
    getLink: compile<StartBotEditGetLinkParams>(
      INSTAGRAM_COMMENT_GUARD_EDIT_PATH,
    ),
  },
  instagramLiveEdit: {
    path: INSTAGRAM_LIVE_EDIT_PATH,
    component: InstagramLive,
    getLink: compile<StartBotEditGetLinkParams>(INSTAGRAM_LIVE_EDIT_PATH),
  },
  commentGuards: {
    path: COMMENT_GUARD_PATH,
    component: CommentGuardPanel,
    getLink: compile<{ chatbotId: number }>(COMMENT_GUARD_PATH),
  },
  commentGuardEdit: {
    path: COMMENT_GUARD_EDIT_PATH,
    component: EditCommentGuardPanel,
    getLink: compile<StartBotEditGetLinkParams>(COMMENT_GUARD_EDIT_PATH),
  },
  leadPopups: {
    path: LEAD_POPUPS_PATH,
    component: LeadsPromoterPanel,
    getLink: compile<{ chatbotId: number }>(LEAD_POPUPS_PATH),
  },
  leadPopupsEdit: {
    path: LEAD_POPUPS_EDIT_PATH,
    component: LeadsPromoterPanelEdit,
    getLink: compile<StartBotEditGetLinkParams>(LEAD_POPUPS_EDIT_PATH),
  },
  leadPopupsNew: {
    path: LEAD_POPUPS_NEW_PATH,
    component: CreateLeadOptIn,
    getLink: compile<{ chatbotId: number }>(LEAD_POPUPS_NEW_PATH),
  },
  shareLinks: {
    path: SHARE_LINKS_PATH,
    component: PromotersPanel,
    getLink: compile<{ chatbotId: number }>(SHARE_LINKS_PATH),
  },
  shareLinkEdit: {
    path: SHARE_LINKS_EDIT_PATH,
    component: EditPromoterPanel,
    getLink: compile<StartBotEditGetLinkParams>(SHARE_LINKS_EDIT_PATH),
  },
  facebookOptIns: {
    path: FACEBOOK_OPTINS_PATH,
    component: PromotersPanel,
    getLink: compile<{ chatbotId: number }>(FACEBOOK_OPTINS_PATH),
  },
  messengerAds: {
    path: '/chatbot-editor/:chatbotId/click-to-messenger-ads',
    component: MessengerAdsPanel,
    getLink: ({ chatbotId }: { chatbotId: number }) =>
      `/chatbot-editor/${chatbotId}/click-to-messenger-ads`,
  },
  ctmAdsEdit: {
    path: `/chatbot-editor/:chatbotId/ctm-ads/edit/:adId`,
    component: CtmAdDetail,
    getLink: compile<{ chatbotId: number; adId: number }>(
      '/chatbot-editor/:chatbotId/ctm-ads/edit/:adId',
    ),
  },
  sendToMessengerEdit: {
    path: SEND_TO_MESSENGER_PATH,
    component: EditPromoterPanel,
    getLink: compile<StartBotEditGetLinkParams>(SEND_TO_MESSENGER_PATH),
  },
  checkboxPluginEdit: {
    path: CHECKBOCX_PLUGIN_PATH,
    component: EditPromoterPanel,
    getLink: compile<StartBotEditGetLinkParams>(CHECKBOCX_PLUGIN_PATH),
  },
  facebookPageWelcomer: {
    path: FACEBOOK_PAGE_WELCOMER,
    component: FacebookPageWelcomerOverview,
    getLink: compile<{ chatbotId: number }>(FACEBOOK_PAGE_WELCOMER),
  },
  dripCampaignEdit: {
    path: '/chatbot-editor/:chatbotId/drip-campaign-detail/:campaignId?',
    component: DripCampaignDetailsPanel,
    getLink: ({
      chatbotId,
      campaignId,
    }: {
      chatbotId: number;
      campaignId?: number;
    }) => {
      const conditionalPath = campaignId ? `/${campaignId}` : '';
      return `/chatbot-editor/${chatbotId}/drip-campaign-detail${conditionalPath}`;
    },
  },
  chatBlastEdit: {
    path: CHAT_BLASTS_EDIT_PATH,
    component: ChatBlasterDetailsPanel,
    getLink: compile<{
      chatbotId: number;
      campaignId: number;
      dialogueId?: number;
    }>(CHAT_BLASTS_EDIT_PATH),
  },
  sponsoredMessagesEdit: {
    path: SPONSORED_MESSAGES_EDIT_PATH,
    component: PromoBlasterDetails,
    getLink: compile<{ chatbotId: number; id: number }>(
      SPONSORED_MESSAGES_EDIT_PATH,
    ),
  },
  smsKeywordEdit: {
    path: SMS_KEYWORD_EDIT_PATH,
    component: EditSMSKeywordPanel,
    getLink: compile<{
      chatbotId: number;
      dialogueId: number;
      smsKeywordId: number;
    }>(SMS_KEYWORD_EDIT_PATH),
  },
  chatStarters: {
    path: CHAT_STARTERS_PATH,
    component: ChatStarters,
    getLink: ({
      chatbotId,
      kind,
    }: {
      chatbotId: number;
      kind?: UIFilterStarterKindType;
    }) => {
      const path = compile<{ chatbotId: number }>(CHAT_STARTERS_PATH)({
        chatbotId,
      });
      return kind ? `${path}?${stringify({ kind })}` : path;
    },
  },
  automationRules: {
    path: AUTOMATION_RULES_PATH,
    component: AutomationRules,
    getLink: ({
      chatbotId,
      kind,
    }: {
      chatbotId: number;
      kind: UIFilterAutomationRuleKindType;
    }) => {
      const path = compile(AUTOMATION_RULES_PATH)({ chatbotId });
      return kind ? `${path}?${stringify({ kind })}` : path;
    },
  },
  campaigns: {
    path: CAMPAIGNS_PATH,
    component: Campaigns,
    getLink: ({
      chatbotId,
      kind,
    }: {
      chatbotId: number;
      kind: UIFilterCampaignKindType;
    }) => {
      const path = compile(CAMPAIGNS_PATH)({ chatbotId });
      return kind ? `${path}?${stringify({ kind })}` : path;
    },
  },
  audiences: {
    path: AUDIENCES_PATH,
    component: Audiences,
    getLink: ({
      chatbotId,
      audienceId,
    }: {
      chatbotId: number;
      audienceId?: number;
    }) => {
      const path = compile(AUDIENCES_PATH)({ chatbotId });
      return audienceId
        ? `${path}?${stringify({ audienceId, pageNumber: 1 })}`
        : path;
    },
  },
  contacts: {
    path: CONTACTS_PATH,
    component: Contacts,
    getLink: compile<{ chatbotId: number }>(CONTACTS_PATH),
  },
  xRay: {
    path: XRAY_PATH,
    component: XRayLeads,
    getLink: compile<{ chatbotId: number }>(XRAY_PATH),
  },
  leadsSearch: {
    path: MERCURY_PATH,
    component: LeadsSearch,
    getLink: compile<{ chatbotId: number }>(MERCURY_PATH),
  },
  integrations: {
    path: INTEGRATIONS_PATH,
    component: IntegrationsPanel,
    getLink: compile<{ chatbotId: number }>(INTEGRATIONS_PATH),
  },
  botAnalytics: {
    path: BOT_ANALYTICS_PATH,
    component: BotAnalytics,
    getLink: compile<{ chatbotId: number }>(BOT_ANALYTICS_PATH),
  },
  adsAnalytics: {
    path: ADS_ANALYTICS_PATH,
    component: AdsAnalytics,
    getLink: compile<{ chatbotId: number }>(ADS_ANALYTICS_PATH),
  },
  emailAnalytics: {
    path: EMAIL_ANALYTICS_PATH,
    component: EmailAnalytics,
    getLink: compile<{ chatbotId: number }>(EMAIL_ANALYTICS_PATH),
  },
  xrayAnalytics: {
    path: XRAY_ANALYTICS_PATH,
    component: XRayAnalytics,
    getLink: compile<{ chatbotId: number }>(XRAY_ANALYTICS_PATH),
  },
  landingPagesEdit: {
    path: LANDING_PAGES_EDIT_PATH,
    component: LandingPageDetails,
    getLink: compile<{ chatbotId: number; landingPageId: number }>(
      LANDING_PAGES_EDIT_PATH,
    ),
  },
  campaignReport: {
    path: CAMPAIGN_REPORT_PATH,
    component: CampaignReportPanel,
    getLink:
      compile<{ chatbotId: number; campaignId: number }>(CAMPAIGN_REPORT_PATH),
  },
  settings: {
    path: SETTINGS_PATH,
    component: Settings,
    getLink: compile<{ chatbotId: number }>(SETTINGS_PATH),
  },
  settingsChatbot: {
    path: SETTINGS_CHATBOT_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_CHATBOT_PATH),
  },
  settingsInbox: {
    path: SETTINGS_INBOX_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_INBOX_PATH),
  },
  settingsCustomerChatWidget: {
    path: SETTINGS_CUSTOMER_CHAT_WIDGET_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_CUSTOMER_CHAT_WIDGET_PATH),
  },
  settingsSystemMessages: {
    path: SETTINGS_SYSTEM_MESSAGES_PATH,
  },
  settingsAdvertising: {
    path: SETTINGS_ADVERTISING_PATH,
  },
  settingsUsers: {
    path: SETTINGS_USERS_PATH,
  },
  settingsMaintenance: {
    path: SETTINGS_MAINTENANCE_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_MAINTENANCE_PATH),
  },
  settingsSms: {
    path: SETTINGS_SMS_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_SMS_PATH),
  },
  settingsSmsAreaCodeSearch: {
    path: SETTINGS_SMS_AREA_CODE_SEARCH_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_SMS_AREA_CODE_SEARCH_PATH),
  },
  settingsSmsActivate: {
    path: SETTINGS_SMS_ACTIVATE_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_SMS_ACTIVATE_PATH),
  },
  settingsSmsNumber: {
    path: SETTINGS_SMS_VIEW_NUMBER_PATH,
    getLink: compile<{ chatbotId: number }>(SETTINGS_SMS_VIEW_NUMBER_PATH),
  },
  settingsTemplates: {
    path: SETTINGS_TEMPLATES_PATH,
  },
  settingsMMApi: {
    path: SETTINGS_MM_API_PATH,
  },
  templatesLibrary: {
    path: TEMPLATES_LIBRARY_PATH,
  },
  templatesFindByCode: {
    path: TEMPLATES_FIND_BY_CODE_PATH,
  },
  myTemplates: {
    path: MY_TEMPLATES_PATH,
  },
  rssFeeds: {
    path: RSS_FEEDS_PATH,
    component: RssFeedsPanel,
    getLink: compile<{ chatbotId: number }>(RSS_FEEDS_PATH),
  },
  rssFeed: {
    path: RSS_FEED_PATH,
    component: RssFeedDetails,
    getLink: compile<{ chatbotId: number; rssFeedId: number }>(RSS_FEED_PATH),
  },
  connectionsEdit: {
    path: CONNECTIONS_EDIT_PATH,
    component: EditConnectionPanel,
    getLink: compile<{ chatbotId: number; connectionId: number }>(
      CONNECTIONS_EDIT_PATH,
    ),
  },
  liveChat: {
    path: LIVE_CHAT_PATH,
    component: LiveChat,
    getLink: (
      {
        chatbotId,
        remoteUserId,
      }: {
        chatbotId: number;
        remoteUserId: string;
      },
      options?: StringifyOptions,
    ) => {
      const path = compile<{ chatbotId: number }>(LIVE_CHAT_PATH)({
        chatbotId,
      });
      return remoteUserId
        ? `${path}?${stringify({ remoteUserId }, options)}`
        : path;
    },
  },
  instagramWelcomer: {
    path: INSTAGRAM_WELCOMER_PATH,
    component: PromotersPanel,
    getLink: compile<{ chatbotId: number }>(INSTAGRAM_WELCOMER_PATH),
  },
  instagramWelcomerEdit: {
    path: INSTAGRAM_WELCOMER__EDIT_PATH,
    component: InstagramWelcomerOverview,
    getLink: compile<StartBotEditGetLinkParams>(INSTAGRAM_WELCOMER__EDIT_PATH),
  },
  restore: {
    path: RESTORE_PATH,
    component: Restore,
    getLink: compile<{ chatbotId: number }>(RESTORE_PATH),
  },
};
